import React from 'react';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { Report } from '../../../../types/firebase';
import { convertTimestamp } from '../../../../types/firebase';
import {
  getReportSiteNameDisplay,
  getScannerDisplayLabel,
} from '../../../../utils/reportPrivacyDisplay';

interface RecentReportsCardProps {
  reports: Report[];
  viewerMasterOwnerId?: string;
}

const isSkimmer = (r: Report) =>
  r.status === 'Skimmer Detected!' || r.status === 'Skimmer Detected';

const StatusIcon: React.FC<{ report: Report }> = ({ report }) => {
  if (isSkimmer(report)) {
    return <WarningAmberIcon sx={{ fontSize: 14, color: '#ff3b3b' }} />;
  }
  return <CheckCircleIcon sx={{ fontSize: 14, color: '#00e676' }} />;
};

export const RecentReportsCard: React.FC<RecentReportsCardProps> = ({
  reports,
  viewerMasterOwnerId,
}) => {
  const navigate = useNavigate();
  const recent = reports.slice(0, 6);

  const formatDate = (createdAt: Report['createdAt']) => {
    const d = convertTimestamp(createdAt);
    return d.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const siteName = (r: Report) => getReportSiteNameDisplay(r, viewerMasterOwnerId);
  const deviceType = (r: Report) =>
    (r as any).readerDetails?.device?.deviceType || 'Device';
  const reportedBy = (r: Report) => getScannerDisplayLabel(r, viewerMasterOwnerId);

  return (
    <Box className="glass-panel data-card recent-reports-card">
      <h3 className="card-title">Recent Reports</h3>

      <div className="table-responsive">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Site</th>
              <th>Type</th>
              <th>Status</th>
              <th>Reported By</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted" style={{ padding: 24, textAlign: 'center' }}>
                  No reports yet.
                </td>
              </tr>
            ) : (
              recent.map((report) => (
                <tr key={report.id}>
                  <td className="text-primary">{formatDate(report.createdAt)}</td>
                  <td className="td-site">
                    <div className="site-id text-primary">{siteName(report)}</div>
                  </td>
                  <td className="text-primary">{deviceType(report)}</td>
                  <td>
                    <div className="status-cell">
                      <StatusIcon report={report} />
                      <span className={isSkimmer(report) ? 'text-red font-semibold' : 'text-green font-semibold'}>
                        {report.status}
                      </span>
                    </div>
                  </td>
                  <td className="text-muted">{reportedBy(report)}</td>
                  <td className="text-muted">{report.message || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="view-all-container">
        <button
          type="button"
          className="view-all-link"
          onClick={() => navigate('/reports')}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          View All Reports
        </button>
      </div>
    </Box>
  );
};
